import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ArrowRight, Activity, Shield, Eye, AlertTriangle, Zap, Database, BarChart3, Globe, Cpu, Server, Network } from 'lucide-react';

export default function SystemsPage() {
  const { currentLanguage } = useLanguage();

  const content = {
    en: {
      hero: {
        eyebrow: 'Engineering',
        title: 'Systems Engineered by Datex',
        subtitle: 'Production-grade software systems designed for reliability, scale, and long-term operational stability.'
      },
      philosophy: {
        title: 'Core Philosophy',
        items: [
          { icon: Cpu, title: 'Systems before features', desc: 'Architecture decisions drive product quality' },
          { icon: Shield, title: 'Reliability is non-negotiable', desc: 'Every system must survive failure' },
          { icon: Eye, title: 'Observability by default', desc: 'If you can\'t measure it, you can\'t improve it' },
          { icon: AlertTriangle, title: 'Design for failure', desc: 'Assume everything will break eventually' }
        ]
      },
      systems: {
        title: 'Production Systems',
        subtitle: 'Real-world systems currently in operation',
        items: [
          {
            name: 'Borsamex',
            type: 'AI-Powered Trading Signal Engine',
            description: 'Real-time decision pipeline processing market signals and generating trading recommendations with sub-50ms latency.',
            metrics: [
              { label: 'Latency', value: '<50ms' },
              { label: 'Throughput', value: '1M+/day' },
              { label: 'Uptime', value: '99.9%' }
            ],
            tags: ['Real-time ML', 'Signal Processing', 'Decision Engine'],
            link: '/case-studies/borsamex'
          },
          {
            name: 'DatexTrader',
            type: 'Algorithmic Trading Infrastructure',
            description: 'High-availability execution architecture for algorithmic trading with ultra-low latency order routing.',
            metrics: [
              { label: 'Routing', value: '<1ms' },
              { label: 'Capacity', value: '10k/sec' },
              { label: 'Uptime', value: '99.99%' }
            ],
            tags: ['High-Frequency', 'Order Management', 'Risk Control'],
            link: '/case-studies/datextrader'
          },
          {
            name: 'İzmex',
            type: 'Industrial B2B Sourcing Platform',
            description: 'Multi-vendor marketplace with scalable catalog architecture connecting manufacturers to global export markets.',
            metrics: [
              { label: 'Manufacturers', value: '12k+' },
              { label: 'Markets', value: '150+' },
              { label: 'Products', value: '500k+' }
            ],
            tags: ['B2B Marketplace', 'Export Discovery', 'Catalog Engine'],
            link: '/case-studies/izmex'
          }
        ]
      },
      patterns: {
        title: 'Architecture Patterns',
        subtitle: 'Proven patterns we implement across systems',
        items: [
          { icon: Activity, title: 'Real-time data pipelines', desc: 'Stream processing for live data' },
          { icon: Zap, title: 'Decision engines', desc: 'Rule-based and ML-powered logic' },
          { icon: Shield, title: 'Risk-aware architectures', desc: 'Built-in failure boundaries' },
          { icon: BarChart3, title: 'Production analytics', desc: 'Metrics that drive decisions' },
          { icon: Globe, title: 'Custom B2B platforms', desc: 'Multi-tenant enterprise systems' },
          { icon: Server, title: 'API-first systems', desc: 'Composable service architectures' }
        ]
      },
      cta: {
        title: 'Need a system like this?',
        subtitle: 'We design and engineer mission-critical software systems for ambitious teams.',
        button: 'Start a conversation'
      }
    },
    tr: {
      hero: {
        eyebrow: 'Mühendislik',
        title: 'Datex Tarafından Mühendislik Edilen Sistemler',
        subtitle: 'Güvenilirlik, ölçeklenebilirlik ve uzun vadeli operasyonel stabilite için tasarlanmış production-grade yazılım sistemleri.'
      },
      philosophy: {
        title: 'Temel Felsefe',
        items: [
          { icon: Cpu, title: 'Özellikten önce sistem', desc: 'Mimari kararlar ürün kalitesini belirler' },
          { icon: Shield, title: 'Güvenilirlik pazarlık konusu değil', desc: 'Her sistem arızaya dayanmalı' },
          { icon: Eye, title: 'Varsayılan olarak gözlemlenebilirlik', desc: 'Ölçemezsen geliştiremezsin' },
          { icon: AlertTriangle, title: 'Arıza için tasarla', desc: 'Her şeyin eninde sonunda bozulacağını varsay' }
        ]
      },
      systems: {
        title: 'Production Sistemler',
        subtitle: 'Şu anda çalışan gerçek sistemler',
        items: [
          {
            name: 'Borsamex',
            type: 'AI Destekli Trading Sinyal Motoru',
            description: 'Piyasa sinyallerini işleyen ve 50ms altı gecikmeyle trading önerileri üreten gerçek zamanlı karar pipeline\'ı.',
            metrics: [
              { label: 'Gecikme', value: '<50ms' },
              { label: 'Throughput', value: '1M+/gün' },
              { label: 'Uptime', value: '%99.9' }
            ],
            tags: ['Gerçek Zamanlı ML', 'Sinyal İşleme', 'Karar Motoru'],
            link: '/case-studies/borsamex'
          },
          {
            name: 'DatexTrader',
            type: 'Algoritmik Trading Altyapısı',
            description: 'Ultra düşük gecikmeli emir yönlendirmesiyle algoritmik trading için yüksek erişilebilirlikli execution mimarisi.',
            metrics: [
              { label: 'Yönlendirme', value: '<1ms' },
              { label: 'Kapasite', value: '10k/sn' },
              { label: 'Uptime', value: '%99.99' }
            ],
            tags: ['Yüksek Frekans', 'Emir Yönetimi', 'Risk Kontrolü'],
            link: '/case-studies/datextrader'
          },
          {
            name: 'İzmex',
            type: 'Endüstriyel B2B Tedarik Platformu',
            description: 'Üreticileri global ihracat pazarlarına bağlayan ölçeklenebilir katalog mimarisine sahip çok satıcılı pazar yeri.',
            metrics: [
              { label: 'Üretici', value: '12k+' },
              { label: 'Pazar', value: '150+' },
              { label: 'Ürün', value: '500k+' }
            ],
            tags: ['B2B Pazar Yeri', 'İhracat Keşfi', 'Katalog Motoru'],
            link: '/case-studies/izmex'
          }
        ]
      },
      patterns: {
        title: 'Mimari Desenler',
        subtitle: 'Sistemlerimizde uyguladığımız kanıtlanmış desenler',
        items: [
          { icon: Activity, title: 'Gerçek zamanlı veri pipeline\'ları', desc: 'Canlı veri için stream işleme' },
          { icon: Zap, title: 'Karar motorları', desc: 'Kural tabanlı ve ML destekli mantık' },
          { icon: Shield, title: 'Risk-bilinçli mimariler', desc: 'Yerleşik arıza sınırları' },
          { icon: BarChart3, title: 'Production analitiği', desc: 'Kararları yönlendiren metrikler' },
          { icon: Globe, title: 'Özel B2B platformları', desc: 'Çok kiracılı kurumsal sistemler' },
          { icon: Server, title: 'API-first sistemler', desc: 'Birleştirilebilir servis mimarileri' }
        ]
      },
      cta: {
        title: 'Böyle bir sisteme mi ihtiyacınız var?',
        subtitle: 'Hırslı ekipler için kritik görev yazılım sistemleri tasarlıyor ve mühendislik ediyoruz.',
        button: 'Görüşme başlat'
      }
    },
    de: {
      hero: {
        eyebrow: 'Engineering',
        title: 'Von Datex entwickelte Systeme',
        subtitle: 'Produktionsreife Softwaresysteme, entwickelt für Zuverlässigkeit, Skalierbarkeit und langfristige operative Stabilität.'
      },
      philosophy: {
        title: 'Kernphilosophie',
        items: [
          { icon: Cpu, title: 'Systeme vor Features', desc: 'Architekturentscheidungen bestimmen die Produktqualität' },
          { icon: Shield, title: 'Zuverlässigkeit ist nicht verhandelbar', desc: 'Jedes System muss Ausfälle überstehen' },
          { icon: Eye, title: 'Observability by default', desc: 'Was man nicht messen kann, kann man nicht verbessern' },
          { icon: AlertTriangle, title: 'Design für Fehler', desc: 'Gehe davon aus, dass alles irgendwann ausfällt' }
        ]
      },
      systems: {
        title: 'Produktionssysteme',
        subtitle: 'Reale Systeme im aktuellen Betrieb',
        items: [
          {
            name: 'Borsamex',
            type: 'KI-gestützte Trading-Signal-Engine',
            description: 'Echtzeit-Entscheidungspipeline zur Verarbeitung von Marktsignalen und Generierung von Handelsempfehlungen mit unter 50ms Latenz.',
            metrics: [
              { label: 'Latenz', value: '<50ms' },
              { label: 'Durchsatz', value: '1M+/Tag' },
              { label: 'Uptime', value: '99,9%' }
            ],
            tags: ['Echtzeit-ML', 'Signalverarbeitung', 'Entscheidungsengine'],
            link: '/case-studies/borsamex'
          },
          {
            name: 'DatexTrader',
            type: 'Algorithmische Trading-Infrastruktur',
            description: 'Hochverfügbare Ausführungsarchitektur für algorithmischen Handel mit ultraniedrigem Latenz-Orderrouting.',
            metrics: [
              { label: 'Routing', value: '<1ms' },
              { label: 'Kapazität', value: '10k/Sek' },
              { label: 'Uptime', value: '99,99%' }
            ],
            tags: ['Hochfrequenz', 'Orderverwaltung', 'Risikokontrolle'],
            link: '/case-studies/datextrader'
          },
          {
            name: 'İzmex',
            type: 'Industrielle B2B-Beschaffungsplattform',
            description: 'Multi-Vendor-Marktplatz mit skalierbarer Katalogarchitektur, der Hersteller mit globalen Exportmärkten verbindet.',
            metrics: [
              { label: 'Hersteller', value: '12k+' },
              { label: 'Märkte', value: '150+' },
              { label: 'Produkte', value: '500k+' }
            ],
            tags: ['B2B-Marktplatz', 'Export-Entdeckung', 'Katalog-Engine'],
            link: '/case-studies/izmex'
          }
        ]
      },
      patterns: {
        title: 'Architekturmuster',
        subtitle: 'Bewährte Muster, die wir systemübergreifend implementieren',
        items: [
          { icon: Activity, title: 'Echtzeit-Datenpipelines', desc: 'Stream-Verarbeitung für Live-Daten' },
          { icon: Zap, title: 'Entscheidungs-Engines', desc: 'Regelbasierte und ML-gestützte Logik' },
          { icon: Shield, title: 'Risikobewusste Architekturen', desc: 'Eingebaute Fehlergrenzen' },
          { icon: BarChart3, title: 'Produktionsanalytik', desc: 'Metriken, die Entscheidungen antreiben' },
          { icon: Globe, title: 'Maßgeschneiderte B2B-Plattformen', desc: 'Mandantenfähige Unternehmenssysteme' },
          { icon: Server, title: 'API-First-Systeme', desc: 'Komponierbare Service-Architekturen' }
        ]
      },
      cta: {
        title: 'Benötigen Sie ein solches System?',
        subtitle: 'Wir entwerfen und entwickeln geschäftskritische Softwaresysteme für ambitionierte Teams.',
        button: 'Gespräch starten'
      }
    }
  };

  const c = content[currentLanguage] || content.en;

  return (
    <>
      <SEO page="systems" />
      <Navbar />
      <main>
        {/* Hero Section - Dark */}
        <section
          data-testid="systems-hero"
          className="relative min-h-[420px] flex items-center"
          style={{ background: 'var(--bg-base)' }}
        >
          {/* Grid Pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: 'linear-gradient(rgba(230,237,243,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(230,237,243,0.1) 1px, transparent 1px)',
              backgroundSize: '40px 40px'
            }}
          />

          {/* Blueprint Diagram */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1/3 h-full opacity-20 hidden lg:block">
            <svg viewBox="0 0 400 400" className="w-full h-full">
              {/* System nodes */}
              <circle cx="200" cy="100" r="40" fill="none" stroke="#14B8A6" strokeWidth="1" />
              <circle cx="100" cy="250" r="35" fill="none" stroke="#14B8A6" strokeWidth="1" />
              <circle cx="300" cy="250" r="35" fill="none" stroke="#14B8A6" strokeWidth="1" />
              <circle cx="200" cy="350" r="30" fill="none" stroke="#14B8A6" strokeWidth="1" />
              
              {/* Connections */}
              <line x1="200" y1="140" x2="120" y2="220" stroke="#14B8A6" strokeWidth="1" strokeDasharray="4,4" />
              <line x1="200" y1="140" x2="280" y2="220" stroke="#14B8A6" strokeWidth="1" strokeDasharray="4,4" />
              <line x1="100" y1="285" x2="170" y2="330" stroke="#14B8A6" strokeWidth="1" strokeDasharray="4,4" />
              <line x1="300" y1="285" x2="230" y2="330" stroke="#14B8A6" strokeWidth="1" strokeDasharray="4,4" />
              
              {/* Labels */}
              <text x="200" y="105" textAnchor="middle" fill="#14B8A6" fontSize="10" fontFamily="JetBrains Mono">ENGINE</text>
              <text x="100" y="255" textAnchor="middle" fill="#14B8A6" fontSize="9" fontFamily="JetBrains Mono">DATA</text>
              <text x="300" y="255" textAnchor="middle" fill="#14B8A6" fontSize="9" fontFamily="JetBrains Mono">API</text>
              <text x="200" y="355" textAnchor="middle" fill="#14B8A6" fontSize="9" fontFamily="JetBrains Mono">OUTPUT</text>
            </svg>
          </div>

          <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-8 pt-32 pb-16">
            {/* Eyebrow */}
            <div 
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-6"
              style={{ 
                background: 'rgba(15, 61, 62, 0.25)',
                borderColor: 'rgba(28, 124, 125, 0.2)'
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#14B8A6]" />
              <span className="text-xs font-medium tracking-wide" style={{ color: '#14B8A6' }}>
                {c.hero.eyebrow}
              </span>
            </div>

            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6 max-w-3xl"
              style={{ color: 'var(--text-primary)' }}
            >
              {c.hero.title}
            </h1>

            <p 
              className="text-base md:text-lg leading-relaxed max-w-2xl"
              style={{ color: 'var(--text-muted)' }}
            >
              {c.hero.subtitle}
            </p>
          </div>
        </section>

        {/* Philosophy Section - Light */}
        <section className="py-20" style={{ background: '#FFFFFF' }}>
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <h2 
              className="text-2xl font-semibold mb-12 tracking-tight"
              style={{ color: 'var(--text-on-light-primary)' }}
            >
              {c.philosophy.title}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {c.philosophy.items.map((item, i) => (
                <div 
                  key={i}
                  className="p-6 rounded-lg transition-all duration-300 hover:shadow-md"
                  style={{ 
                    background: '#F8FAFB',
                    border: '1px solid rgba(15, 118, 110, 0.1)'
                  }}
                >
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                    style={{ background: 'rgba(20, 184, 166, 0.1)' }}
                  >
                    <item.icon className="w-5 h-5" style={{ color: '#0F766E' }} />
                  </div>
                  <h3 
                    className="text-sm font-semibold mb-2"
                    style={{ color: 'var(--text-on-light-primary)' }}
                  >
                    {item.title}
                  </h3>
                  <p 
                    className="text-xs leading-relaxed"
                    style={{ color: 'var(--text-on-light-muted)' }}
                  >
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Systems Grid - Light */}
        <section className="py-20" style={{ background: '#F8FAFB' }}>
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="mb-12">
              <h2 
                className="text-2xl font-semibold mb-3 tracking-tight"
                style={{ color: 'var(--text-on-light-primary)' }}
              >
                {c.systems.title}
              </h2>
              <p 
                className="text-sm"
                style={{ color: 'var(--text-on-light-muted)' }}
              >
                {c.systems.subtitle}
              </p>
            </div>

            <div className="space-y-6">
              {c.systems.items.map((system, i) => (
                <div 
                  key={i}
                  className="p-8 rounded-xl transition-all duration-300 hover:shadow-lg group"
                  style={{ 
                    background: '#FFFFFF',
                    border: '1px solid rgba(15, 118, 110, 0.1)'
                  }}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                    {/* Left - System Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 
                          className="text-xl font-semibold"
                          style={{ color: 'var(--text-on-light-primary)' }}
                        >
                          {system.name}
                        </h3>
                        <span 
                          className="text-xs px-2 py-1 rounded"
                          style={{ 
                            background: 'rgba(20, 184, 166, 0.1)',
                            color: '#0F766E'
                          }}
                        >
                          Live
                        </span>
                      </div>
                      <p 
                        className="text-sm font-medium mb-3"
                        style={{ color: '#0F766E' }}
                      >
                        {system.type}
                      </p>
                      <p 
                        className="text-sm leading-relaxed mb-4"
                        style={{ color: 'var(--text-on-light-muted)' }}
                      >
                        {system.description}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2">
                        {system.tags.map((tag, j) => (
                          <span 
                            key={j}
                            className="text-xs px-2.5 py-1 rounded-full"
                            style={{ 
                              background: 'rgba(15, 118, 110, 0.08)',
                              color: 'var(--text-on-light-muted)'
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Right - Metrics */}
                    <div className="flex gap-6 lg:gap-8">
                      {system.metrics.map((metric, j) => (
                        <div key={j} className="text-center">
                          <div 
                            className="text-2xl font-bold mb-1"
                            style={{ color: '#0F766E' }}
                          >
                            {metric.value}
                          </div>
                          <div 
                            className="text-xs uppercase tracking-wider"
                            style={{ color: 'var(--text-on-light-muted)' }}
                          >
                            {metric.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* View Architecture Link */}
                  <div className="mt-6 pt-6" style={{ borderTop: '1px solid rgba(15, 118, 110, 0.08)' }}>
                    <Link 
                      to={system.link}
                      className="inline-flex items-center gap-2 text-sm font-medium transition-all duration-300 group-hover:gap-3"
                      style={{ color: '#0F766E' }}
                    >
                      View system architecture
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Architecture Patterns - Light */}
        <section className="py-20" style={{ background: '#FFFFFF' }}>
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="mb-12">
              <h2 
                className="text-2xl font-semibold mb-3 tracking-tight"
                style={{ color: 'var(--text-on-light-primary)' }}
              >
                {c.patterns.title}
              </h2>
              <p 
                className="text-sm"
                style={{ color: 'var(--text-on-light-muted)' }}
              >
                {c.patterns.subtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {c.patterns.items.map((pattern, i) => (
                <div 
                  key={i}
                  className="flex items-start gap-4 p-5 rounded-lg transition-all duration-300 hover:shadow-sm"
                  style={{ 
                    background: '#F8FAFB',
                    border: '1px solid rgba(15, 118, 110, 0.08)'
                  }}
                >
                  <div 
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(20, 184, 166, 0.1)' }}
                  >
                    <pattern.icon className="w-4 h-4" style={{ color: '#0F766E' }} />
                  </div>
                  <div>
                    <h3 
                      className="text-sm font-medium mb-1"
                      style={{ color: 'var(--text-on-light-primary)' }}
                    >
                      {pattern.title}
                    </h3>
                    <p 
                      className="text-xs"
                      style={{ color: 'var(--text-on-light-muted)' }}
                    >
                      {pattern.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section - Dark */}
        <section 
          className="py-20"
          style={{ background: 'var(--bg-base)' }}
        >
          <div className="max-w-6xl mx-auto px-4 md:px-8 text-center">
            <h2 
              className="text-3xl font-semibold mb-4 tracking-tight"
              style={{ color: 'var(--text-primary)' }}
            >
              {c.cta.title}
            </h2>
            <p 
              className="text-base mb-8 max-w-xl mx-auto"
              style={{ color: 'var(--text-muted)' }}
            >
              {c.cta.subtitle}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg font-medium text-sm transition-all duration-300 hover:scale-[1.02]"
              style={{
                background: 'linear-gradient(135deg, #0F3D3E 0%, #1C7C7D 100%)',
                color: '#F0F4F8',
                border: '1px solid rgba(28, 124, 125, 0.4)',
                boxShadow: '0 0 20px rgba(28, 124, 125, 0.15)'
              }}
            >
              {c.cta.button}
              <ArrowRight className="w-4 h-4" />
            </Link>

            {/* Engineered by Datex Systems - Premium Badge */}
            <div className="mt-16 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <p 
                className="text-[13px] italic"
                style={{ 
                  color: 'rgba(255,255,255,0.45)',
                  fontWeight: 500,
                  letterSpacing: '0.05em'
                }}
              >
                Engineered by{' '}
                <Link 
                  to="/systems"
                  className="transition-colors duration-300 hover:text-[#14B8A6]"
                  style={{ color: 'rgba(20, 184, 166, 0.85)' }}
                >
                  Datex Systems
                </Link>
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
